/**
 * CallProvider — the central state hub for 1:1 calling (Phase 2).
 *
 * Wires `call_invite` / `call_state` realtime frames (delivered via
 * `RealtimeProvider.registerCallHandlers`) into React state, and exposes
 * all the REST actions (ring / accept / decline / cancel / hangup) plus
 * helper callbacks (markConnected, clear) through `useCallController()`.
 *
 * Mount this once, below `RealtimeProvider`, in `AppShell`.
 * The `IncomingCallModal` (Task 6) and the rewired `CallWindow` (Task 7)
 * consume this context — do NOT import the REST helpers directly there.
 *
 * See docs/calls.md for the three-plane architecture and the state machine.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import type {
  CallEndReason,
  CallInvitePayload,
  CallRoomCoords,
  CallScreenPayload,
  CallCameraPayload,
  CallRosterPayload,
  CallRosterParticipant,
  CallState,
  CallStatePayload,
} from "@xop/dashboard-shared";
import { callEndedLabel } from "@xop/dashboard-shared";

import { acceptCall, cancelCall, declineCall, endCall, heartbeatCall, ringCall, ringGroupCall, startSoloCall, inviteCall, screenSignal, cameraSignal, reportSid as reportSidApi } from "./api.js";
import { ApiError } from "../lib/api.js";
import { surfaceIncomingCall } from "../lib/notifications/desktopBridge.js";
import { useRealtime } from "../lib/notifications/RealtimeProvider.js";

// ─── Public contracts ────────────────────────────────────────────────────────

export interface ActiveCall {
  callId: string;
  peerName: string;
  state: CallState;        // "ringing" | "connecting" | "connected" | "ended"
  reason?: CallEndReason;
  room?: CallRoomCoords;   // present once state reaches "connecting"
  /** Optional custom blurb for the brief "ended" placeholder (e.g. "Bob is
   * unavailable" when the callee is offline). Falls back to "Call ended". */
  note?: string;
  /** External phone numbers to auto-dial into this call once it connects (the
   * launcher's keypad / CRM-contact targets). `CallWindow` drains them after the
   * webcast token is ready, then `consumePendingNumbers` clears them. */
  pendingNumbers?: string[];
  /** A FRESH outbound PSTN call's primary number — placed as an H&H-style
   * consultation dial (a direct JsSIP INVITE to the number), NOT `dial_out`.
   * `CallWindow` joins via `joinConsultation` when this + `room.moderatorPin`
   * are present; any extra numbers ride `pendingNumbers` (dial_out). */
  consultationNumber?: string;
}

/** The current remote screen-share state as seen by THIS client. */
export interface ScreenState {
  /** Whether the remote party is currently sharing their screen. */
  remoteSharing: boolean;
  /** The conferweb sessionId of the remote sharer, or null when not sharing. */
  remoteSharerSessionId: number | null;
}

/** The remote cameras currently ON, as seen by THIS client. A group call can
 * have many concurrent publishers, so this is a SET of conferweb sids (the
 * sharer of each `call_camera{sharing:true}` frame), not a single peer. */
export interface CameraState {
  /** conferweb sids of every remote participant currently publishing camera. */
  sharingSessionIds: number[];
}

export interface CallControllerValue {
  /** A ring prompt when you are the callee and haven't answered. */
  incoming: CallInvitePayload | null;
  /** The call you're currently in (caller OR accepted callee). */
  active: ActiveCall | null;
  /** Remote screen-share state — updated by `call_screen` frames. */
  screen: ScreenState;
  /** Remote camera state — updated by `call_camera` frames. */
  camera: CameraState;
  /** The participant roster for the active call (group calls) — updated by
   * `call_roster` frames. Empty for a not-yet-live or 1:1 call before any frame. */
  roster: CallRosterParticipant[];
  /** Initiate an outbound call to `peer`. */
  ring: (peer: { user_id: number; name: string }) => Promise<void>;
  /** Start a GROUP call to several users at once (busy ones are skipped). */
  ringGroup: (peers: ReadonlyArray<{ user_id: number; name: string }>) => Promise<void>;
  /** Start a call composed in the launcher: ring any chat `staff` (group call)
   * and/or auto-dial external `numbers` (PSTN). A phone-only call (no staff)
   * spins up a solo room. The numbers are dialled by `CallWindow` once the call
   * connects (see `pendingNumbers`). */
  startCall: (targets: {
    staff: ReadonlyArray<{ user_id: number; name: string }>;
    numbers: ReadonlyArray<string>;
  }) => Promise<void>;
  /** Clear the auto-dial queue after `CallWindow` has dialled it. */
  consumePendingNumbers: () => void;
  /** Add another user to the ONGOING call (rings them in). No-op without an active call. */
  invite: (peer: { user_id: number; name: string }) => Promise<void>;
  /** Accept the current incoming invite. */
  accept: () => Promise<void>;
  /** Decline the current incoming invite. */
  decline: () => Promise<void>;
  /** Cancel an outbound ringing call before the callee answers. */
  cancel: () => Promise<void>;
  /** End an in-progress call. */
  hangup: () => Promise<void>;
  /** CallWindow calls this once media is flowing. */
  markConnected: () => void;
  /** Clear all call state (e.g. after a toast has shown). */
  clear: () => void;
  /** Signal to the broker that we started or stopped sharing our screen.
   * `ownSessionId` is the conferweb sessionId from the `/auth` response.
   * No-op if there is no active call. */
  signalScreenShare: (sharing: boolean, ownSessionId: number) => Promise<void>;
  /** Signal to the broker that we turned our camera on or off (mirrors
   * signalScreenShare). No-op if there is no active call. */
  signalCamera: (sharing: boolean, ownSessionId: number) => Promise<void>;
  /** Report our conferweb SFU session id (from `/auth`) so the server puts it on
   * our roster entry and peers can subscribe to our camera. No-op without a
   * call. */
  reportSid: (sid: number) => Promise<void>;
}

// ─── Context ─────────────────────────────────────────────────────────────────

const CallContext = createContext<CallControllerValue | null>(null);

/** Retrieve the call controller. Throws if used outside `<CallProvider>`. */
export function useCallController(): CallControllerValue {
  const ctx = useContext(CallContext);
  if (!ctx) throw new Error("useCallController must be used inside <CallProvider>");
  return ctx;
}

// ─── Provider ────────────────────────────────────────────────────────────────

const BUSY_CLEAR_MS = 1_500;
const ENDED_CLEAR_MS = 1_500;

const SCREEN_RESET: ScreenState = { remoteSharing: false, remoteSharerSessionId: null };
const CAMERA_RESET: CameraState = { sharingSessionIds: [] };

export function CallProvider({ children }: { children: ReactNode }): JSX.Element {
  const { registerCallHandlers, reportBusy } = useRealtime();

  const [incoming, setIncoming] = useState<CallInvitePayload | null>(null);
  const [active, setActive] = useState<ActiveCall | null>(null);
  const [screen, setScreen] = useState<ScreenState>(SCREEN_RESET);
  const [camera, setCamera] = useState<CameraState>(CAMERA_RESET);
  const [roster, setRoster] = useState<CallRosterParticipant[]>([]);

  // Stable refs so the realtime callbacks can read current state without
  // capturing stale closures.
  const activeRef = useRef<ActiveCall | null>(active);
  const incomingRef = useRef<CallInvitePayload | null>(incoming);
  useEffect(() => { activeRef.current = active; }, [active]);
  useEffect(() => { incomingRef.current = incoming; }, [incoming]);

  // Presence: while we're on a live call (ringing / connecting / connected —
  // NOT an ended placeholder, NOT a merely-ringing INCOMING invite), tell the
  // realtime layer we're "busy" so auto-away is suppressed. Watching a screen
  // share is a connected call, so it's covered. When the call clears we report
  // not-busy, which grants a fresh idle window.
  useEffect(() => {
    reportBusy(active != null && active.state !== "ended");
  }, [active, reportBusy]);

  // ── Liveness: heartbeat + leave-fast ──────────────────────────────────────
  // While a call is live, ping the broker every few seconds. If WE stop pinging
  // (refresh / lost connection / closed tab), the broker's staleness sweep ends
  // the call so the OTHER party isn't stuck in a dead call. (Server-side reaper
  // in callSessions.sweepStale.)
  useEffect(() => {
    if (!active || active.state === "ended") return;
    const id = active.callId;
    void heartbeatCall(id).catch(() => {});
    const t = setInterval(() => { void heartbeatCall(id).catch(() => {}); }, 4_000);
    return () => clearInterval(t);
  }, [active?.callId, active?.state]);

  // Fast path: when the tab is refreshed / navigated away / closed, flush a
  // keepalive "end" so the peer is freed immediately instead of waiting for the
  // staleness sweep. Best-effort (keepalive fetch survives unload); the sweep is
  // the guarantee for hard crashes / network loss where pagehide never fires.
  useEffect(() => {
    const onHide = (): void => {
      const act = activeRef.current;
      if (act && act.state !== "ended") void endCall(act.callId, true).catch(() => {});
    };
    window.addEventListener("pagehide", onHide);
    return () => window.removeEventListener("pagehide", onHide);
  }, []);

  // ── Realtime frame handlers ───────────────────────────────────────────────

  const onCallInvite = useCallback((p: CallInvitePayload) => {
    setIncoming(p);
    // Desktop only: raise + focus the app window and fire the native
    // "X is calling" banner. No-op in the browser (the modal + ringtone cover it).
    void surfaceIncomingCall(p.from_user_name);
  }, []);

  const onCallState = useCallback((p: CallStatePayload) => {
    if (p.state === "connecting" && p.room) {
      // Caller learns the callee accepted — move active to connecting with room.
      setActive((prev) => {
        if (prev?.callId !== p.call_id) return prev;
        return { ...prev, state: "connecting", ...(p.room ? { room: p.room } : {}) };
      });
      // Multi-device: if THIS device was ringing the same call but isn't the one
      // that answered (no matching active), the call went live on another of our
      // devices — stop ringing here. Harmless no-op on the device that accepted
      // (its `incoming` is already cleared).
      setIncoming((prev) => (prev?.call_id === p.call_id ? null : prev));
      return;
    }

    if (p.state === "ended") {
      // Active call ended (declined / missed / cancelled / completed / failed).
      setActive((prev) => {
        if (prev?.callId !== p.call_id) return prev;
        return {
          ...prev,
          state: "ended",
          ...(p.reason !== undefined ? { reason: p.reason } : {}),
        };
      });
      // Reset screen-share + camera + roster state when the call ends.
      setScreen(SCREEN_RESET);
      setCamera(CAMERA_RESET);
      setRoster([]);
      setTimeout(() => {
        setActive((prev) => (prev?.callId === p.call_id ? null : prev));
      }, ENDED_CLEAR_MS);

      // If the same call_id matches an incoming invite (caller cancelled / timed out
      // before we answered), clear that too.
      setIncoming((prev) => (prev?.call_id === p.call_id ? null : prev));
    }
  }, []);

  const onCallScreen = useCallback((p: CallScreenPayload) => {
    if (p.sharing) {
      setScreen({ remoteSharing: true, remoteSharerSessionId: p.sharer_session_id });
    } else {
      setScreen(SCREEN_RESET);
    }
  }, []);

  const onCallCamera = useCallback((p: CallCameraPayload) => {
    // Accumulate/remove this publisher's sid — a group has many concurrent ones.
    setCamera((prev) => {
      const has = prev.sharingSessionIds.includes(p.sharer_session_id);
      if (p.sharing) {
        return has ? prev : { sharingSessionIds: [...prev.sharingSessionIds, p.sharer_session_id] };
      }
      return has
        ? { sharingSessionIds: prev.sharingSessionIds.filter((s) => s !== p.sharer_session_id) }
        : prev;
    });
  }, []);

  const onCallRoster = useCallback((p: CallRosterPayload) => {
    const act = activeRef.current;
    // Only adopt the roster for the call we're actually in.
    if (act && act.callId === p.call_id) setRoster(p.participants);
  }, []);

  // Register with RealtimeProvider on mount; clean up on unmount.
  useEffect(() => {
    return registerCallHandlers({ onCallInvite, onCallState, onCallScreen, onCallCamera, onCallRoster });
  }, [registerCallHandlers, onCallInvite, onCallState, onCallScreen, onCallCamera, onCallRoster]);

  // ── REST actions ─────────────────────────────────────────────────────────

  const ring = useCallback(async (peer: { user_id: number; name: string }) => {
    try {
      const { callId } = await ringCall(peer.user_id, peer.name);
      setActive({ callId, peerName: peer.name, state: "ringing" });
    } catch (err) {
      // Show an ended placeholder briefly then clear. Errors come from the shared
      // api() wrapper as typed ApiError. CALL_OFFLINE (callee holds no connection)
      // is distinct from CALL_BUSY — both are 409, so check the code first.
      const offline = err instanceof ApiError && err.code === "CALL_OFFLINE";
      const isBusy =
        !offline &&
        ((err instanceof ApiError && err.status === 409) ||
          (err instanceof Error && err.message.includes("409")));
      const reason: CallEndReason = isBusy ? "busy" : "failed";
      const note = offline ? `${peer.name || "They"} ${peer.name ? "is" : "are"} offline` : undefined;
      const placeholderId = `busy-${Date.now()}`;
      setActive({ callId: placeholderId, peerName: peer.name, state: "ended", reason, ...(note ? { note } : {}) });
      setTimeout(() => {
        setActive((prev) => (prev?.callId === placeholderId ? null : prev));
      }, BUSY_CLEAR_MS);
    }
  }, []);

  const ringGroup = useCallback(async (peers: ReadonlyArray<{ user_id: number; name: string }>) => {
    if (peers.length === 0) return;
    const label = peers.length === 1 ? peers[0]!.name : `Group call (${peers.length})`;
    try {
      const { callId } = await ringGroupCall(peers.map((p) => ({ id: p.user_id, name: p.name })));
      setActive({ callId, peerName: label, state: "ringing" });
      // Seed the roster optimistically (server confirms via call_roster).
      setRoster(peers.map((p) => ({ user_id: p.user_id, name: p.name, state: "ringing" })));
    } catch (err) {
      const offline = err instanceof ApiError && err.code === "CALL_OFFLINE";
      const isBusy =
        !offline &&
        ((err instanceof ApiError && err.status === 409) ||
          (err instanceof Error && err.message.includes("409")));
      const reason: CallEndReason = isBusy ? "busy" : "failed";
      const note = offline
        ? peers.length === 1
          ? `${label} is offline`
          : "Everyone you called is offline"
        : undefined;
      const placeholderId = `busy-${Date.now()}`;
      setActive({ callId: placeholderId, peerName: label, state: "ended", reason, ...(note ? { note } : {}) });
      setTimeout(() => {
        setActive((prev) => (prev?.callId === placeholderId ? null : prev));
      }, BUSY_CLEAR_MS);
    }
  }, []);

  const startCall = useCallback(
    async ({
      staff,
      numbers,
    }: {
      staff: ReadonlyArray<{ user_id: number; name: string }>;
      numbers: ReadonlyArray<string>;
    }) => {
      const nums = numbers.map((n) => n.trim()).filter((n) => n.length > 0);

      // Any staff selected → ring them (reusing the group path so the
      // busy/offline error handling stays in ONE place); attach the PSTN numbers
      // so CallWindow dials them once the call connects.
      if (staff.length > 0) {
        await ringGroup(staff);
        if (nums.length > 0) {
          setActive((prev) =>
            prev && prev.state === "ringing" ? { ...prev, pendingNumbers: nums } : prev,
          );
        }
        return;
      }

      // Phone-only call: nobody to ring, so spin up a solo room. The FIRST number
      // is dialled as an H&H-style consultation (a direct JsSIP INVITE — see
      // CallWindow/joinConsultation); any extras are added via dial_out once
      // connected (the "add to a call" path the user asked to keep).
      if (nums.length === 0) return;
      const label = nums.length === 1 ? (nums[0] ?? "Call") : `Call (${nums.length})`;
      const [first, ...rest] = nums;
      try {
        const { callId, room } = await startSoloCall();
        // Start in `ringing` (not `connecting`) so the caller hears ringback while
        // the dialed party rings; CallWindow's consultation answer-poll flips it to
        // `connected` (markConnected) only once the far end is off-hook.
        setActive({
          callId,
          peerName: label,
          state: "ringing",
          room,
          ...(first ? { consultationNumber: first } : {}),
          ...(rest.length > 0 ? { pendingNumbers: rest } : {}),
        });
      } catch {
        const placeholderId = `busy-${Date.now()}`;
        setActive({ callId: placeholderId, peerName: label, state: "ended", reason: "failed" });
        setTimeout(() => {
          setActive((prev) => (prev?.callId === placeholderId ? null : prev));
        }, BUSY_CLEAR_MS);
      }
    },
    [ringGroup],
  );

  const consumePendingNumbers = useCallback(() => {
    setActive((prev) => {
      if (!prev?.pendingNumbers) return prev;
      const next = { ...prev };
      delete next.pendingNumbers;
      return next;
    });
  }, []);

  const invite = useCallback(async (peer: { user_id: number; name: string }) => {
    const act = activeRef.current;
    if (!act) return;
    await inviteCall(act.callId, peer.user_id, peer.name);
    // Show them as ringing right away; the server's call_roster confirms/updates.
    setRoster((prev) =>
      prev.some((p) => p.user_id === peer.user_id)
        ? prev
        : [...prev, { user_id: peer.user_id, name: peer.name, state: "ringing" }],
    );
  }, []);

  const accept = useCallback(async () => {
    const inv = incomingRef.current;
    if (!inv) return;
    try {
      const { room } = await acceptCall(inv.call_id);
      setActive({
        callId: inv.call_id,
        peerName: inv.from_user_name,
        state: "connecting",
        room,
      });
      setIncoming(null);
    } catch (err) {
      // Always drop the modal so it can't get stuck ringing.
      setIncoming((prev) => (prev?.call_id === inv.call_id ? null : prev));

      // A 409 is BENIGN: the call was already answered on another of our devices
      // (race) or the caller hung up first — nothing failed, stay quiet.
      const benign = err instanceof ApiError && err.status === 409;
      if (benign) return;

      // Anything else is a REAL failure — most often 502 CALL_ROOM_FAILED, the
      // media room could not be provisioned. Say so instead of swallowing it:
      // silently closing the modal is what made accepting a call look like it
      // "just does nothing". The caller is told separately by the server's
      // `ended`/`failed` frame.
      const placeholderId = `failed-${inv.call_id}`;
      setActive({
        callId: placeholderId,
        peerName: inv.from_user_name,
        state: "ended",
        reason: "failed",
        note: callEndedLabel("failed"),
      });
      setTimeout(() => {
        setActive((prev) => (prev?.callId === placeholderId ? null : prev));
      }, BUSY_CLEAR_MS);
    }
  }, []);

  const decline = useCallback(async () => {
    const inv = incomingRef.current;
    if (!inv) return;
    await declineCall(inv.call_id);
    setIncoming(null);
  }, []);

  const cancel = useCallback(async () => {
    const act = activeRef.current;
    if (!act) return;
    await cancelCall(act.callId);
    setActive(null);
  }, []);

  const hangup = useCallback(async () => {
    const act = activeRef.current;
    if (!act) return;
    // The user wants OUT — always tear down the local call, even if the server end
    // call fails (e.g. the session was already gone). Leaving `active` set on a
    // rejected end was the "call can no longer be hung up" bug. The server /end is
    // also idempotent now (always 200), so this is belt-and-suspenders.
    try {
      await endCall(act.callId);
    } catch {
      /* best-effort: tear down regardless */
    } finally {
      setActive(null);
    }
  }, []);

  const markConnected = useCallback(() => {
    setActive((prev) => (prev ? { ...prev, state: "connected" } : prev));
  }, []);

  const clear = useCallback(() => {
    setIncoming(null);
    setActive(null);
    setScreen(SCREEN_RESET);
    setCamera(CAMERA_RESET);
    setRoster([]);
  }, []);

  const signalScreenShare = useCallback(async (sharing: boolean, ownSessionId: number) => {
    const act = activeRef.current;
    if (!act) return;
    await screenSignal(act.callId, sharing, ownSessionId);
  }, []);

  const signalCamera = useCallback(async (sharing: boolean, ownSessionId: number) => {
    const act = activeRef.current;
    if (!act) return;
    await cameraSignal(act.callId, sharing, ownSessionId);
  }, []);

  const reportSid = useCallback(async (sid: number) => {
    const act = activeRef.current;
    if (!act) { console.info(`[call] reportSid SKIPPED — no active call (sid=${sid})`); return; }
    try {
      await reportSidApi(act.callId, sid);
      console.info(`[call] reportSid OK callId=${act.callId} sid=${sid}`);
    } catch (e) {
      // Don't let a failed sid report become an unhandled rejection — and surface
      // it, since a silent failure here is exactly what hid the roster-sid bug.
      console.info(`[call] reportSid FAILED callId=${act.callId} sid=${sid}: ${(e as Error)?.message ?? e}`);
    }
  }, []);

  // ── Context value ─────────────────────────────────────────────────────────

  const value: CallControllerValue = {
    incoming,
    active,
    screen,
    camera,
    roster,
    ring,
    ringGroup,
    startCall,
    consumePendingNumbers,
    invite,
    accept,
    decline,
    cancel,
    hangup,
    markConnected,
    clear,
    signalScreenShare,
    signalCamera,
    reportSid,
  };

  return <CallContext.Provider value={value}>{children}</CallContext.Provider>;
}
